import {
  create_empty_committee_form,
  create_empty_decision_form,
  create_empty_action_form,
  create_empty_accountability_form,
  create_empty_calendar_form,
  create_empty_consequence_form,
  create_committee_card,
  build_action_row,
  build_accountability_row,
  build_calendar_row,
  build_consequence_row,
  build_decision_row
} from './governance_builders';

function value_or_default(value, fallback_value) {
  const resolved_value = value || fallback_value;
  return resolved_value;
}

function action_form_from_row(row) {
  const action = value_or_default(row.action, '');
  const owner = value_or_default(row.owner, '');
  const raised_in = value_or_default(row.raised_in, 'Board');
  const due = value_or_default(row.due, '');
  const status = value_or_default(row.status, 'Open');
  const action_form = { action, owner, raised_in, due, status };
  return action_form;
}

function accountability_form_from_row(row) {
  const accountability_form = {
    area: value_or_default(row.area, ''),
    accountable: value_or_default(row.accountable, ''),
    responsible: value_or_default(row.responsible, ''),
    oversight: value_or_default(row.oversight, ''),
    proof: value_or_default(row.proof, ''),
    status: value_or_default(row.status, 'On track')
  };
  return accountability_form;
}

function decision_form_from_row(row) {
  const decided_in = value_or_default(row.decided_in, value_or_default(row.approved_by, 'Board'));
  const under_authority = value_or_default(row.under_authority, value_or_default(row.authorises, ''));
  const linked_fields = {
    linked_risk: value_or_default(row.linked_risk, ''),
    linked_policy: value_or_default(row.linked_policy, ''),
    linked_evidence: value_or_default(row.linked_evidence, '')
  };
  const decision_form = {
    decision: value_or_default(row.decision, ''),
    decided_in,
    approved_by: value_or_default(row.approved_by, ''),
    accountable_owner: value_or_default(row.accountable_owner, ''),
    under_authority,
    authorises: value_or_default(row.authorises, ''),
    ...linked_fields,
    meeting_date: value_or_default(row.meeting_date, ''),
    status: value_or_default(row.status, 'Passed'),
    note: value_or_default(row.note, '')
  };
  return decision_form;
}

function calendar_form_from_row(row) {
  const calendar_form = {
    event: value_or_default(row.event, ''),
    type: value_or_default(row.type, 'Board meeting'),
    date: value_or_default(row.date, ''),
    owner: value_or_default(row.owner, ''),
    status: value_or_default(row.status, 'Scheduled'),
    icon: value_or_default(row.icon, 'bi-building')
  };
  return calendar_form;
}

function consequence_form_from_row(row) {
  const consequence_form = {
    breach: value_or_default(row.breach, ''),
    responsible_party: value_or_default(row.responsible_party, ''),
    severity: value_or_default(row.severity, 'Medium'),
    action_taken: value_or_default(row.action_taken, ''),
    corrective_step: value_or_default(row.corrective_step, ''),
    status: value_or_default(row.status, 'Open')
  };
  return consequence_form;
}

function governance_form_from_row(kind, row) {
  if (!row) {
    const empty_form_factories = {
      action: create_empty_action_form,
      accountability: create_empty_accountability_form,
      calendar: create_empty_calendar_form,
      consequence: create_empty_consequence_form,
      decision: create_empty_decision_form
    };
    const create_form = Reflect.get(empty_form_factories, kind) || create_empty_decision_form;
    const empty_form = create_form();
    return empty_form;
  }

  const form_builders = {
    action: () => action_form_from_row(row),
    accountability: () => accountability_form_from_row(row),
    calendar: () => calendar_form_from_row(row),
    consequence: () => consequence_form_from_row(row),
    decision: () => decision_form_from_row(row)
  };
  const build_form = Reflect.get(form_builders, kind) || Reflect.get(form_builders, 'decision');
  const governance_form = build_form();
  return governance_form;
}

function governance_row_config(vm, kind) {
  const row_configs = {
    action: { rows: vm.action_rows, builder: build_action_row, prefix: 'action', sequence_key: 'action_row_seq' },
    accountability: { rows: vm.accountability_rows, builder: build_accountability_row, prefix: 'accountability', sequence_key: 'accountability_row_seq' },
    calendar: { rows: vm.calendar_rows, builder: build_calendar_row, prefix: 'calendar', sequence_key: 'calendar_row_seq' },
    consequence: { rows: vm.consequence_rows, builder: build_consequence_row, prefix: 'consequence', sequence_key: 'consequence_row_seq' }
  };
  const row_config = Reflect.get(row_configs, kind) || null;
  return row_config;
}

function upsert_governance_row(rows, index, row, is_edit) {
  if (is_edit) {
    rows.splice(index, 1, row);
    return;
  }
  rows.push(row);
}

function build_decision_identifiers(vm, is_edit, index) {
  if (is_edit) {
    const existing_row = Reflect.get(vm.decision_rows, index) || {};
    const existing_identifiers = { id: existing_row.id, ref: existing_row.ref };
    return existing_identifiers;
  }
  vm.decision_row_seq += 1;
  const decision_identifiers = {
    id: `decision-${vm.decision_row_seq}`,
    ref: `RES-${210 + vm.decision_row_seq}`
  };
  return decision_identifiers;
}

function build_row_identifier(vm, config, is_edit, index) {
  if (is_edit) {
    const existing_row = Reflect.get(config.rows, index) || {};
    return existing_row.id;
  }
  const next_sequence = vm[config.sequence_key] + 1;
  vm[config.sequence_key] = next_sequence;
  const row_identifier = `${config.prefix}-${next_sequence}`;
  return row_identifier;
}

function committee_form_from_card(card) {
  if (!card) return create_empty_committee_form();
  const committee_form = {
    name: value_or_default(card.title, ''),
    type: value_or_default(card.type, 'Board'),
    chairperson: value_or_default(card.chairperson, ''),
    members: value_or_default(card.members, ''),
    meeting_cadence: value_or_default(card.meeting_cadence, 'Monthly'),
    quorum: value_or_default(card.quorum, ''),
    status: value_or_default(card.status, 'Active'),
    mandate: value_or_default(card.mandate, value_or_default(card.copy, ''))
  };
  return committee_form;
}

function governance_delete_target_rows(context) {
  const config = governance_row_config(context, context.governance_modal_kind);
  const target_rows = config ? config.rows : context.decision_rows;
  return target_rows;
}

function next_committee_sequence(context, existing_id) {
  if (existing_id) return null;
  const next_sequence = context.committee_card_seq + 1;
  context.committee_card_seq = next_sequence;
  return next_sequence;
}

function modal_delete_blocked(mode, index) {
  const is_blocked = mode !== 'edit' || index === null;
  return is_blocked;
}

function browser_delete_cancelled(message) {
  const is_cancelled = typeof window !== 'undefined' && !window.confirm(message);
  return is_cancelled;
}

export const governance_methods = {
  set_tab(tab_id) {
    this.close_decision_drawer();
    this.close_governance_modal();
    this.close_committee_modal();
    this.active_tab = tab_id;
  },
  adjust_maturity(row, delta) {
    if (!row || typeof row.value !== 'number' || typeof row.max !== 'number') return;
    row.value = Math.max(0, Math.min(row.max, row.value + delta));
  },
  open_decision_drawer(row = null, index = null) {
    if (!row) return;
    this.close_committee_modal();
    this.close_governance_modal();
    const active_decision_drawer_ref = value_or_default(row.ref, value_or_default(row.id, null));
    this.active_decision_drawer_ref = active_decision_drawer_ref;
    this.active_decision_drawer_index = typeof index === 'number' ? index : null;
    this.$nextTick(() => {
      const drawer_body = this.$refs.decisionDrawerBody;
      if (drawer_body) drawer_body.scrollTop = 0;
    });
  },
  close_decision_drawer() {
    this.active_decision_drawer_ref = null;
    this.active_decision_drawer_index = null;
  },
  edit_active_decision() {
    if (!this.active_decision_row) return;
    this.open_governance_modal('decision', this.active_decision_row, this.active_decision_drawer_index);
  },
  open_governance_modal(kind, row = null, index = null) {
    this.close_committee_modal();
    this.close_decision_drawer();
    this.governance_modal_kind = kind;
    this.governance_modal_mode = row ? 'edit' : 'create';
    this.governance_modal_index = typeof index === 'number' ? index : null;
    this.governance_modal_form = governance_form_from_row(kind, row);
    this.governance_modal_open = true;
  },
  close_governance_modal() {
    this.governance_modal_open = false;
    this.governance_modal_kind = 'decision';
    this.governance_modal_mode = 'create';
    this.governance_modal_index = null;
    this.governance_modal_form = create_empty_decision_form();
  },
  save_governance_modal() {
    const is_edit = this.governance_modal_mode === 'edit' && this.governance_modal_index !== null;
    const index = this.governance_modal_index;

    if (this.governance_modal_kind === 'decision') {
      const identifiers = build_decision_identifiers(this, is_edit, index);
      const row = build_decision_row(this.governance_modal_form, identifiers);
      upsert_governance_row(this.decision_rows, index, row, is_edit);
    } else {
      const config = governance_row_config(this, this.governance_modal_kind);
      const id = build_row_identifier(this, config, is_edit, index);
      const row = config.builder(this.governance_modal_form, id);
      upsert_governance_row(config.rows, index, row, is_edit);
    }

    this.close_governance_modal();
  },
  delete_governance_modal() {
    if (modal_delete_blocked(this.governance_modal_mode, this.governance_modal_index)) return;
    if (browser_delete_cancelled('Delete this item?')) return;
    const target_rows = governance_delete_target_rows(this);
    target_rows.splice(this.governance_modal_index, 1);

    this.close_governance_modal();
  },
  open_committee_modal(card = null, index = null) {
    this.close_governance_modal();
    this.close_decision_drawer();
    this.committee_modal_mode = card ? 'edit' : 'create';
    this.committee_modal_index = typeof index === 'number' ? index : null;
    this.committee_modal_form = committee_form_from_card(card);
    this.committee_modal_open = true;
  },
  close_committee_modal() {
    this.committee_modal_open = false;
    this.committee_modal_mode = 'create';
    this.committee_modal_index = null;
    this.committee_modal_form = create_empty_committee_form();
  },
  save_committee_modal() {
    const is_edit = this.committee_modal_mode === 'edit' && this.committee_modal_index !== null;
    const existing_id = is_edit ? this.committee_cards[this.committee_modal_index]?.id : null;
    const next_sequence = next_committee_sequence(this, existing_id);
    const card = create_committee_card(this.committee_modal_form, { id: existing_id, sequence: next_sequence });
    upsert_governance_row(this.committee_cards, this.committee_modal_index, card, is_edit);
    this.close_committee_modal();
    this.active_tab = 'committees';
  },
  delete_committee_modal() {
    if (modal_delete_blocked(this.committee_modal_mode, this.committee_modal_index)) return;
    if (browser_delete_cancelled('Delete this committee?')) return;
    this.committee_cards.splice(this.committee_modal_index, 1);
    this.close_committee_modal();
  }
};
